import { useLocation } from 'react-router-dom';
import { routeTitles, isIdSegment, matchRoutePattern, titleFromPathSegment } from '../components/Layout/Breadcrumb/Breadcrumb';
import { useRoutePageMetaSnapshot } from './pageMetaStore';
import { isHelpCenterCategoryPath, isHelpCenterManagePath } from '../pages/HelpCenter/helpCenterRoutes';

interface IPageTitleResult {
  title: string | null;
  subtitle: string | null;
}

export const usePageTitle = (): IPageTitleResult => {
  const { pathname } = useLocation();
  const dynamicPageMeta = useRoutePageMetaSnapshot(pathname);

  const noTitleRoutes = [
    '/advanced-member-search',
    '/',
    '/login',
    '/forgot-password',
    '/reset-password',
    '/support-test-reset-password',
    '/confirmlogin',
    '/two-factor-auth',
    '/auth/google/callback',
    '/auth/linkedin/callback',
    '/link-account-login',
    '/newUser',
    '/test-icon'
  ].map(route => route.toLowerCase());

  const pathnameLower = pathname.toLowerCase();
  if (noTitleRoutes.some(route => pathnameLower === route || pathnameLower.startsWith(route + '/'))) {
    return { title: null, subtitle: null };
  }

  if (dynamicPageMeta?.title) {
    return {
      title: dynamicPageMeta.title,
      subtitle: dynamicPageMeta.subtitle ?? null,
    };
  }

  if (isHelpCenterCategoryPath(pathname)) {
    return { title: null, subtitle: null };
  }

  if (isHelpCenterManagePath(pathname)) {
    return {
      title: routeTitles['/help-center/manage'] ?? 'Manage WSG Help Center',
      subtitle: null,
    };
  }

  const exactMatch = routeTitles[pathname.toLowerCase()];
  if (exactMatch) {
    return { title: exactMatch, subtitle: null };
  }

  const patternMatch = matchRoutePattern(pathname, routeTitles);
  if (patternMatch) {
    return { title: patternMatch, subtitle: null };
  }

  const pathSegments = pathname.split('/').filter(Boolean);
  // Bulk EmailLog detail ids are `emailsend-…` (not UUID) — keep page title stable.
  if (
    pathSegments[0]?.toLowerCase() === 'email-stats' &&
    pathSegments.length >= 2
  ) {
    return {
      title: routeTitles['/email-stats/id'] ?? 'Email Details',
      subtitle: null,
    };
  }

  if (pathSegments.length > 0) {
    const lastSegment = pathSegments[pathSegments.length - 1];
    if (!isIdSegment(lastSegment)) {
      return {
        title: titleFromPathSegment(lastSegment),
        subtitle: null,
      };
    }

    let currentPath = '';
    let lastTitle = null;

    pathSegments.forEach(segment => {
      currentPath += `/${segment}`;

      if (routeTitles[currentPath.toLowerCase()]) {
        lastTitle = routeTitles[currentPath.toLowerCase()];
      } else {
        const patternTitle = matchRoutePattern(currentPath, routeTitles);
        if (patternTitle) {
          lastTitle = patternTitle;
        }
      }
    });

    if (lastTitle) {
      return { title: lastTitle, subtitle: null };
    }

    for (let i = pathSegments.length - 1;i >= 0;i--) {
      const segment = pathSegments[i];
      if (!isIdSegment(segment)) {
        return {
          title: titleFromPathSegment(segment),
          subtitle: null,
        };
      }
    }
  }

  return { title: null, subtitle: null };
};

